import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toTxn } from '../transactions/transactionWire'
import type { TxnPage } from '../transactions/useTransactions'

export type ReviewSummary = { pendingCount: number; autoTaggedCount: number; refreshedAt: Date | null }

/** The endpoint's own ceiling on one request (`transaction_ids` is 1..500). */
export const REVIEW_MAX_IDS = 500
/** Page size of the pending list, and so how many rows `/review` shows at once. */
export const PENDING_PAGE_SIZE = 100

const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
/** "3:47 pm", in the viewer's own timezone: the time the last refresh landed. Copy, never a stale marker (Q7). */
export const refreshTime = (d: Date): string => TIME.format(d).replace(/\s?([AP])M$/, (_m, c: string) => ` ${c.toLowerCase()}m`)

export class ReviewError extends Error {
  readonly status: number | undefined
  constructor(cause: unknown, status?: number) {
    super('review-unavailable', { cause })
    this.name = 'ReviewError'
    this.status = status
  }
}

/**
 * One of the ids no longer exists as a visible row (a bank reversal, or a pending row replaced by
 * its posted one). The endpoint is all-or-nothing, so NOTHING moved.
 */
export class ReviewConflictError extends Error {
  constructor() {
    super('review-conflict')
    this.name = 'ReviewConflictError'
  }
}

/**
 * The inbox counts. **Not date-scoped**, so it can disagree with the list header's per-month
 * "N to review" (Q1), and each reads its own source. `refreshed_at` is copy (Q7), never `stale`.
 */
export function useReviewSummary(): AsyncState<ReviewSummary> {
  const query = useQuery({
    queryKey: keys.review.summary(),
    queryFn: async ({ signal }): Promise<ReviewSummary> => {
      const { data, error, response } = await api.GET('/api/v1/transactions/review/summary', { signal })
      if (error !== undefined || data === undefined) throw new ReviewError(error, response.status)
      const at = data.refreshed_at === null || data.refreshed_at === undefined ? null : new Date(data.refreshed_at)
      return {
        pendingCount: data.pending_count,
        autoTaggedCount: data.auto_tagged_count,
        refreshedAt: at !== null && !Number.isNaN(at.getTime()) ? at : null,
      }
    },
  })
  return fromQuery(query, {
    message: () => 'The review summary could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * Page 1 of the pending rows, which is what `/review` shows. **Held where it is**: no refetch on
 * focus and no cache beyond the screen's life, because a category change moves a row to
 * `reviewed` on the server and a refetch would make it jump out from under the user's cursor.
 */
export function usePendingReview(): AsyncState<TxnPage> {
  const query = useQuery({
    queryKey: keys.review.pending(),
    queryFn: async ({ signal }): Promise<TxnPage> => {
      const { data, error, response } = await api.GET('/api/v1/transactions', {
        params: { query: { review_status: 'pending', page: 1, page_size: PENDING_PAGE_SIZE } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new ReviewError(error, response.status)
      return { rows: data.transactions.map(toTxn), total: data.total, page: data.page, hasMore: data.has_more }
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
  })
  return fromQuery(query, {
    message: () => 'The transactions to review could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The ids behind a card-level Dismiss: pending rows, a page at a time, up to the endpoint's own
 * ceiling. Returns what it found and whether more pending rows exist beyond it.
 */
export async function fetchPendingIds(): Promise<{ ids: string[]; truncated: boolean }> {
  const ids: string[] = []
  const pages = REVIEW_MAX_IDS / PENDING_PAGE_SIZE
  for (let page = 1; page <= pages; page++) {
    const { data, error, response } = await api.GET('/api/v1/transactions', {
      params: { query: { review_status: 'pending', page, page_size: PENDING_PAGE_SIZE } },
    })
    if (error !== undefined || data === undefined) throw new ReviewError(error, response.status)
    ids.push(...data.transactions.map((t) => t.id))
    if (!data.has_more) return { ids, truncated: false }
  }
  return { ids, truncated: true }
}

/**
 * `POST /transactions/review`. All or nothing: a 404 means one id is unknown and NOTHING moved, so
 * it rejects with `ReviewConflictError` after refetching what the screen shows. Success
 * invalidates the list, the count and the inbox, so a stale Overview-style miss cannot happen.
 */
export function useMarkReviewed(): {
  run: (ids: string[], status: 'reviewed' | 'dismissed') => Promise<number>
  pending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: 'reviewed' | 'dismissed' }) => {
      if (ids.length === 0 || ids.length > REVIEW_MAX_IDS) throw new ReviewError(new Error('ids out of range'))
      const { data, error, response } = await api.POST('/api/v1/transactions/review', {
        body: { transaction_ids: ids, status },
      })
      if (response.status === 404) throw new ReviewConflictError()
      if (error !== undefined || data === undefined) throw new ReviewError(error, response.status)
      return data.updated
    },
    onSettled: (_data, error) => {
      // A conflict means the screen's rows are out of date, and a success means they are done.
      if (error === null || error instanceof ReviewConflictError) {
        void client.invalidateQueries({ queryKey: keys.review.all() })
        void client.invalidateQueries({ queryKey: keys.transactions.all() })
        void client.invalidateQueries({ queryKey: keys.cashflow.all() })
      }
    },
  })
  return { run: (ids, status) => mutation.mutateAsync({ ids, status }), pending: mutation.isPending }
}
